import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "input", "preview", "placeholder", "container" ]
  static values = {
    maxFiles: { type: Number, default: 10 }
  }

  connect() {
    this.files = new Set()
    this.bindDragAndDrop()
    this.element.addEventListener("click", this.handleClick.bind(this))
  }

  disconnect() {
    this.unbindDragAndDrop()
    this.element.removeEventListener("click", this.handleClick.bind(this))
  }

  bindDragAndDrop() {
    this.element.addEventListener("dragover", this.handleDragOver.bind(this))
    this.element.addEventListener("drop", this.handleDrop.bind(this))
    this.element.addEventListener("dragenter", this.handleDragEnter.bind(this))
    this.element.addEventListener("dragleave", this.handleDragLeave.bind(this))
  }

  unbindDragAndDrop() {
    this.element.removeEventListener("dragover", this.handleDragOver.bind(this))
    this.element.removeEventListener("drop", this.handleDrop.bind(this))
    this.element.removeEventListener("dragenter", this.handleDragEnter.bind(this))
    this.element.removeEventListener("dragleave", this.handleDragLeave.bind(this))
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.add("border-indigo-500")
  }

  handleDragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.add("border-indigo-500")
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.remove("border-indigo-500")
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.remove("border-indigo-500")

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    // Create a new DataTransfer object and add the dropped files
    const dataTransfer = new DataTransfer()
    files.forEach(file => dataTransfer.items.add(file))
    
    // Update the file input
    this.inputTarget.files = dataTransfer.files
    
    // Trigger the change event to process the files
    this.handleFileSelect({ target: this.inputTarget })
  }

  handleFileSelect(event) {
    const newFiles = Array.from(event.target.files || [])
    if (newFiles.length === 0) return

    if (this.files.size + newFiles.length > this.maxFilesValue) {
      alert(`You can only upload up to ${this.maxFilesValue} files`)
      return
    }

    // Create a new DataTransfer to combine existing and new files
    const dataTransfer = new DataTransfer()
    
    // Add existing files
    this.files.forEach(file => dataTransfer.items.add(file))
    
    // Add new files
    newFiles.forEach(file => {
      this.files.add(file)
      dataTransfer.items.add(file)
      this.createPreviewCard(file)
    })

    // Update the input with all files
    this.inputTarget.files = dataTransfer.files
    this.updatePlaceholder()
  }

  createPreviewCard(file) {
    const card = document.createElement("div")
    card.className = "preview-card relative bg-gray-800 rounded-lg overflow-hidden"
    card.dataset.fileName = file.name

    const preview = document.createElement("div")
    preview.className = "w-full h-32 bg-cover bg-center"
    
    const removeButton = document.createElement("button")
    removeButton.className = "absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg"
    removeButton.innerHTML = "×"
    removeButton.type = "button"
    removeButton.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.removeFile(file.name)
    })

    const fileName = document.createElement("div")
    fileName.className = "absolute bottom-0 left-0 right-0 bg-gray-800 bg-opacity-75 text-white p-2 text-xs truncate"
    fileName.textContent = file.name

    card.appendChild(preview)
    card.appendChild(removeButton)
    card.appendChild(fileName)
    this.containerTarget.appendChild(card)

    this.loadPreview(file, preview)
  }

  loadPreview(file, element) {
    const reader = new FileReader()
    reader.onload = (e) => {
      element.style.backgroundImage = `url('${e.target.result}')`
    }
    reader.readAsDataURL(file)
  }

  removeFile(fileName) {
    // Remove from Set
    this.files.forEach(file => {
      if (file.name === fileName) {
        this.files.delete(file)
      }
    })

    // Update input files
    const dataTransfer = new DataTransfer()
    this.files.forEach(file => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files

    // Remove preview
    const card = this.containerTarget.querySelector(`[data-file-name="${fileName}"]`)
    if (card) card.remove()

    this.updatePlaceholder()
  }

  resetDropzone() {
    this.files = new Set()
    this.containerTarget.innerHTML = ""
    this.inputTarget.value = ""
    this.updatePlaceholder()
  }

  updatePlaceholder() {
    if (this.files.size === 0) {
      this.placeholderTarget.style.display = "flex"
    } else {
      this.placeholderTarget.style.display = "none"
    }
  }

  handleClick(event) {
    // If clicking on a preview card or its children, prevent file input from opening
    if (event.target.closest('.preview-card')) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    // If clicking anywhere else in the dropzone, trigger file input
    if (event.target !== this.inputTarget) {
      event.preventDefault()
      this.inputTarget.click()
    }
  }
} 